import { useState, useEffect, useRef } from 'react';
import { getStoreFrontUrl, getStoreDomain } from '@/lib/utils/store-domain';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  Tag,
  CreditCard,
  ArrowRight,
  CheckCircle2,
  Loader2,
  User,
  MapPin,
  Gift,
  Wallet,
  Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCart } from '@/contexts/CartContext';
import { cupomGuardado, esquecerCupomDoLink, podeAplicarCupomDoLink } from '@/lib/cupom-do-link';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import PageHero from '@/components/common/PageHero';
import { CheckoutModal } from '@/components/checkout/CheckoutModal';
import { toast } from 'sonner';
import { useAvatar } from '@/hooks/useAvatar';
import { PaymentMethod } from '@/types';
import {
  paymentService,
  PaymentProvider,
  CheckoutResponse,
} from '@/lib/services/payment.service';
import { obterTokenDoCartao, type DadosCartao } from '@/lib/picpay-sdk';
import { CardForm } from '@/components/checkout/CardForm';
import { PicPayLightbox } from '@/components/checkout/PicPayLightbox';

import { Caixa } from '@/construtor/caixa';
export default function CartPage() {
  const { t } = useTranslation();
  const {
    items,
    itemCount,
    subtotal,
    discount,
    total,
    coupon,
    hasCreditItem,
    updateQuantity,
    removeItem,
    removeCredit,
    removeVipUpgrade,
    applyCoupon,
    removeCoupon,
    clearCart,
    refreshCart,
    quote,
    quoting,
  } = useCart();
  const { config } = useTheme();
  const { store } = useStore();
  const { user, isAuthenticated, openLoginModal, updateCredits } = useAuth();
  const { getAvatarUrl } = useAvatar();

  const [couponCode, setCouponCode] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('');
  /*
   * CPF pedido no checkout, não no cadastro.
   *
   * O PicPay exige documento do comprador e o jogador não tem esse campo na
   * conta. Pedir no cadastro afastaria quem só quer criar conta; pedir na hora
   * de pagar é o que qualquer brasileiro já espera de uma compra online.
   */
  const [buyerDocument, setBuyerDocument] = useState('');

  /** Só o PicPay exige documento hoje; o Mercado Pago não pede. */
  const exigeDocumento = paymentProvider === 'picpay';

  const [dadosCartao, setDadosCartao] = useState<DadosCartao>({
    numero: '',
    nome: '',
    documento: '',
    mesValidade: '',
    anoValidade: '',
    cvv: '',
  });
  const [parcelas, setParcelas] = useState(1);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const [availableProviders, setAvailableProviders] = useState<PaymentProvider[]>([]);
  const providerEscolhido = availableProviders.find((p) => p.provider === paymentProvider);

  /**
   * O cartão só é pedido quando dá para tokenizar.
   *
   * Sem as credenciais do SDK o navegador não converteria o cartão em token, e
   * o jogador digitaria tudo para receber uma recusa. O backend também esconde
   * `CREDIT_CARD` da lista nesse caso; esta é a segunda tranca.
   */
  /**
   * O formulário de cartão só existe no modo transparente.
   *
   * Nos modos padrão e lightbox quem recebe o cartão é a página do PicPay —
   * pedir aqui seria coletar dado que não vamos usar, e nos colocaria no
   * escopo PCI sem necessidade nenhuma. A presença das credenciais de
   * tokenização é o sinal: o backend só as envia no transparente.
   */
  const exigeCartao =
    paymentMethod === 'CREDIT_CARD' && Boolean(providerEscolhido?.tokenization);
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState('');
  const [currentPrimaryDomain, setCurrentPrimaryDomain] = useState('');
  const navigate = useNavigate();
  
  // Credits state
  const [useCredits, setUseCredits] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState(0);
  const [customCreditsInput, setCustomCreditsInput] = useState('');

  // Format value as Brazilian currency (mask)
  const formatCurrencyInput = (value: string): string => {
    // Remove everything except digits
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    
    // Convert to number (cents) and then to reais
    const numericValue = parseInt(digits, 10) / 100;
    
    // Format as Brazilian currency without the symbol
    return numericValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Parse masked currency value back to number
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    // Remove dots (thousands separator) and replace comma with dot (decimal)
    const normalized = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const handleCreditsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrencyInput(e.target.value);
    setCustomCreditsInput(formatted);
  };

  // Calculate user's available credits
  const userCredits = user?.credits || 0;
  
  // Calculate total after coupon discount (before credits)
  const totalAfterCoupon = Math.max(0, subtotal - discount);
  
  // Calculate max credits that can be used (can't exceed total after coupon)
  const maxCreditsToUse = Math.min(userCredits, totalAfterCoupon);
  
  // Calculate final total after credits
  const finalTotal = Math.max(0, totalAfterCoupon - creditsAmount);

  const problemas = quote?.problems ?? [];
  const linhaDaCotacao = (productId?: string | null) =>
    productId ? quote?.lines.find((l) => l.productId === productId) : undefined;
  const problemaDoItem = (productId?: string | null) =>
    productId ? problemas.find((p) => p.productId === productId) : undefined;

  // Cupom que veio pelo link (`?cupom=`): aplica uma vez, quando o carrinho
  // já tem itens e o jogador está na conta. Se não valer para este carrinho, o
  // código fica no campo para ele ver o motivo e tentar de novo.
  const cupomDoLinkTentado = useRef(false);
  useEffect(() => {
    if (!podeAplicarCupomDoLink({ autenticado: isAuthenticated, itens: items.length, temCupom: !!coupon, jaTentou: cupomDoLinkTentado.current })) return;
    const codigo = cupomGuardado();
    if (!codigo) return;
    cupomDoLinkTentado.current = true;
    esquecerCupomDoLink();
    void applyCoupon(codigo).then((resultado) => {
      if (resultado.success) toast.success(resultado.message);
      else {
        setCouponCode(codigo);
        toast.error(resultado.message);
      }
    });
  }, [isAuthenticated, items.length, coupon, applyCoupon]);

  // Se o total diminuiu (quantidade menor, cupom novo), o crédito escolhido
  // não pode ficar maior que o que resta pagar.
  useEffect(() => {
    if (creditsAmount > maxCreditsToUse) {
      setCreditsAmount(Math.max(0, Math.floor(maxCreditsToUse * 100) / 100));
    }
  }, [creditsAmount, maxCreditsToUse]);

  // Load available payment providers
  useEffect(() => {
    const loadProviders = async () => {
      if (!store) {
        return;
      }

      try {
        setLoadingProviders(true);
        
        const primaryDomain = getStoreDomain(store);

        if (!primaryDomain) {
          toast.error('Domínio da loja não encontrado');
          return;
        }

        const providers = await paymentService.getPaymentMethods(primaryDomain);
        setAvailableProviders(providers);
        
        // Set default provider if available
        if (providers.length > 0) {
          setPaymentProvider(providers[0].provider);
        }
      } catch (error) {
        console.error('Error loading payment providers:', error);
        toast.error('Erro ao carregar formas de pagamento');
      } finally {
        setLoadingProviders(false);
      }
    };

    loadProviders();
  }, [store]);

  const formatPrice = (price: number) => {
    // Backend sends values already in reais (not cents)
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);
  };

  const formatCredits = (credits: number) => {
    // Credits are already in reais (same as price formatting)
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(credits);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Digite um código de cupom');
      return;
    }
    const result = await applyCoupon(couponCode);
    if (result.success) {
      toast.success(result.message);
      setCouponCode('');
    } else {
      toast.error(result.message);
    }
  };

  const handleUseAllCredits = () => {
    setCreditsAmount(maxCreditsToUse);
    setUseCredits(true);
    setCustomCreditsInput('');
  };

  const handleUseCustomCredits = () => {
    const amount = parseCurrencyInput(customCreditsInput);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Digite um valor válido');
      return;
    }
    if (amount > userCredits) {
      toast.error('Você não tem créditos suficientes');
      return;
    }
    if (amount > totalAfterCoupon) {
      toast.error('O valor não pode ser maior que o total');
      return;
    }
    setCreditsAmount(amount);
    setUseCredits(true);
    setCustomCreditsInput('');
  };

  const handleRemoveCredits = () => {
    setCreditsAmount(0);
    setUseCredits(false);
    setCustomCreditsInput('');
  };

  const handleCheckout = async () => {
    if (!store) {
      toast.error('Loja não carregada');
      return;
    }

    // Nunca finaliza com um valor que ainda está sendo recalculado.
    if (quoting || !quote) {
      toast.error(t('store.cart.recalculating'));
      return;
    }

    if (quote.problems.length > 0) {
      toast.error(quote.problems[0].message);
      return;
    }

    // Only require payment provider if finalTotal > 0
    if (finalTotal > 0 && !paymentProvider) {
      toast.error(t('store.cart.no_payment_provider'));
      return;
    }

    /*
     * Valida antes de criar o pedido.
     *
     * Sem isso o pedido nasceria e a cobrança falharia em seguida, deixando um
     * pedido pendente que o jogador não pediu. É só contagem de dígitos: a
     * validação real é do gateway, e duplicá-la aqui só criaria duas verdades.
     */
    if (finalTotal > 0 && exigeDocumento) {
      const digitos = buyerDocument.replace(/\D/g, '');

      if (digitos.length !== 11 && digitos.length !== 14) {
        toast.error(t('store.cart.invalid_document'));
        return;
      }
    }

    if (exigeCartao) {
      const faltando =
        !dadosCartao.numero.trim() ||
        !dadosCartao.nome.trim() ||
        !dadosCartao.mesValidade.trim() ||
        !dadosCartao.anoValidade.trim() ||
        !dadosCartao.cvv.trim();

      if (faltando) {
        toast.error(t('store.cart.card_incomplete'));
        return;
      }
    }

    try {
      setLoading(true);

      /*
       * Tokeniza o cartão **antes** de criar o pedido.
       *
       * Duas razões. A primeira é de segurança: o número do cartão nunca pode
       * chegar a um servidor da Hystore, então ele vira token aqui, no
       * navegador, e só o token segue adiante — é o que nos mantém fora do
       * escopo PCI-DSS.
       *
       * A segunda é de ordem: cartão inválido ou SDK fora do ar falham aqui,
       * antes de existir pedido. Criar o pedido primeiro deixaria um pendente
       * que o jogador não pediu toda vez que ele errasse um dígito.
       *
       * O token vale 5 minutos, tempo de sobra para as duas chamadas
       * seguintes.
       */
      let cardToken: string | undefined;

      if (exigeCartao) {
        try {
          cardToken = await obterTokenDoCartao(providerEscolhido!.tokenization!, {
            ...dadosCartao,
            // O portador do cartão é quem informou o documento no checkout.
            documento: buyerDocument,
          });
        } catch (erro) {
          // A mensagem vem do SDK e fala do cartão, não da nossa infra.
          toast.error(
            erro instanceof Error ? erro.message : t('store.cart.card_token_failed'),
          );
          setLoading(false);
          return;
        }
      }

      // Prefer custom domain for all redirect URLs
      const primaryDomain = getStoreDomain(store);
      const baseUrl = getStoreFrontUrl(store);

      if (!primaryDomain) {
        toast.error('Domínio da loja não encontrado');
        return;
      }

      // Use user from AuthContext (not localStorage directly)
      if (!user) {
        toast.error('Você precisa estar logado para finalizar a compra');
        openLoginModal();
        return;
      }

      // Step 1: Create order from cart (with coupon and credits if applied)
      // playerEmail and playerName are loaded from JWT on the backend — do not send them
      const orderResponse = await paymentService.createOrderFromCart(primaryDomain, {
        couponCode: coupon?.code,
        creditsToUse: creditsAmount > 0 ? creditsAmount : undefined,
        // O servidor confere: se não for este o total, o pedido não é criado.
        expectedTotal: quote.total,
      });

      // If order is already PAID (100% discount or fully paid with credits), go directly to success
      // Credits are deducted on the backend only for free orders
      if (orderResponse.status === 'PAID' || orderResponse.total === 0) {
        // Update local credits balance only for free orders (paid immediately)
        if (creditsAmount > 0) {
          updateCredits(-creditsAmount);
        }
        toast.success('Pedido confirmado! Seu pedido foi processado com sucesso.');
        clearCart();
        window.location.href = `${baseUrl}/payment/success?order_id=${orderResponse.orderId}&free=true`;
        return;
      }

      // For orders that need payment, credits will be deducted when payment is approved (webhook)

      // Step 2: Create checkout for the order
      // Mercado Pago requires HTTPS URLs for back_urls
      setCurrentOrderId(orderResponse.orderId);
      setCurrentPrimaryDomain(primaryDomain);

      // Clear cart immediately so items don't reappear if the user navigates back.
      // The DB cart was already cleared by the use case; this clears local state.
      clearCart();

      const response = await paymentService.createCheckout(primaryDomain, {
        orderId: orderResponse.orderId,
        method: paymentMethod as PaymentMethod,
        provider: paymentProvider,
        ...(buyerDocument.trim() ? { buyerDocument: buyerDocument.trim() } : {}),
        ...(cardToken ? { cardToken, installments: parcelas } : {}),
        successUrl: `${baseUrl}/payment/success${hasCreditItem ? '?credit=true' : ''}`,
        failureUrl: `${baseUrl}/payment/failure`,
        pendingUrl: `${baseUrl}/payment/pending`,
      });

      /*
       * Como apresentar o resultado vem do gateway, não de uma lista de
       * métodos aqui.
       *
       * Antes era `if (paymentMethod === 'MERCADO_PAGO')`, e cada gateway novo
       * exigia mais uma linha nesse `if` — o PicPay sozinho traria três casos.
       * O `presentation` é decidido por quem sabe: o provider.
       *
       * O `checkoutUrl` já foi validado no backend contra os hosts do gateway
       * antes de chegar aqui.
       */
      const apresentacao =
        response.presentation ??
        (paymentMethod === 'MERCADO_PAGO' && response.checkoutUrl ? 'REDIRECT' : undefined);

      if (apresentacao === 'REDIRECT' && response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
        return;
      }

      if (apresentacao === 'IFRAME' && response.checkoutUrl) {
        setLightboxUrl(response.checkoutUrl);
        return;
      }

      // QR code e afins continuam no modal.
      setCheckoutData(response);
      setShowCheckoutModal(true);
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 409 && data?.code === 'ORDER_TOTAL_CHANGED') {
        // Carrinho, preço ou cupom mudou: atualiza a tela e pede nova confirmação.
        await refreshCart();
        toast.error(data?.message || t('store.cart.total_changed'));
        return;
      }

      if (status === 422 && data?.code === 'CART_HAS_PROBLEMS') {
        // Mínimo, limite ou promoção: atualiza o carrinho, que mostra o que ajustar.
        await refreshCart();
        toast.error(data?.problems?.[0]?.message || data?.message || t('store.cart.fix_problems'));
        return;
      }

      if (status === 422 && typeof data?.code === 'string' && data.code.startsWith('COUPON_')) {
        // Cupom recusado no pedido: sai da tela e o total volta a ser o sem cupom.
        removeCoupon();
        toast.error(data?.message || t('store.cart.coupon_removed'));
        return;
      }

      toast.error(
        data?.message || 'Erro ao criar pagamento'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutSuccess = () => {
    setShowCheckoutModal(false);
    clearCart();
  };

  if (items.length === 0 && !showCheckoutModal) {
    return (
      <div>
        <PageHero
          title="Carrinho"
          breadcrumbs={[{ label: 'Carrinho' }]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
          <Caixa id="carrinho.vazio" miolo>
          <Card className="bg-card border-border max-w-lg mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="font-display font-bold text-xl text-foreground mb-2">
                Seu carrinho está vazio
              </h2>
              <p className="text-muted-foreground mb-6">
                Adicione produtos ao carrinho para continuar
              </p>
              <Link to="/loja">
                <Button className="bg-primary hover:bg-primary-hover text-primary-foreground">
                  Ir para a Loja
                </Button>
              </Link>
            </CardContent>
          </Card>
          </Caixa>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Carrinho"
        breadcrumbs={[{ label: 'Carrinho' }]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <Caixa id="carrinho.itens">
          <div className="lg:col-span-2 space-y-4">
            <Caixa id="carrinho.itens.titulo">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl text-foreground">
                {itemCount} {itemCount === 1 ? 'item' : 'itens'} no carrinho
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCart}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar
              </Button>
            </div>
            </Caixa>

            {/* Delivery Info Card */}
            <Caixa id="carrinho.jogador" miolo>
            <Card className="bg-card border-border mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Entrega para</p>
                    {isAuthenticated && user ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={getAvatarUrl(user.username, 128)} />
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {user.username?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-semibold text-foreground text-lg">
                          {user.username}
                        </span>
                        <Badge variant="outline" className="text-xs text-success border-success/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Confirmado
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Você precisa estar logado</span>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="h-auto p-0 text-primary"
                          onClick={openLoginModal}
                        >
                          Fazer Login
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {isAuthenticated && user && (
                  <p className="text-xs text-muted-foreground mt-3 ml-15 pl-[60px]">
                    {hasCreditItem
                      ? t('store.cart.credit_item_description')
                      : 'Os itens serão entregues automaticamente na sua conta do jogo após a confirmação do pagamento.'}
                  </p>
                )}
              </CardContent>
            </Card>
            </Caixa>

            {items.map((item) => {
              if (item.type === 'VIP_UPGRADE') {
                const amount = item.customAmount ?? item.product?.price ?? 0;
                const indisponivel = item.vipUpgrade && !item.vipUpgrade.available;
                return (
                  <Caixa id="carrinho.itens.item" repetida key="vip-upgrade">
                  <Card key="vip-upgrade" className="bg-card border-border" data-testid="cart-vip-upgrade">
                    <CardContent className="p-4">
                      <div className="flex gap-4 items-center">
                        <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Crown className="w-10 h-10 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground line-clamp-1">{item.product?.name}</p>
                          <p className={`text-sm ${indisponivel ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {indisponivel ? item.vipUpgrade?.reason || t('store.vip.upgrade.unavailable') : t('store.vip.upgrade.cart_description')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {!indisponivel && <p className="font-display font-bold text-lg text-primary">{formatPrice(amount)}</p>}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeVipUpgrade}
                            aria-label={t('store.vip.upgrade.remove')}
                            className="text-destructive hover:text-destructive h-auto p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                );
              }

              if (item.type === 'CREDIT') {
                const amount = item.customAmount ?? item.product?.price ?? 0;
                return (
                  <Caixa id="carrinho.itens.credito" key={"credit-topup"}>
                  <Card key="credit-topup" className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex gap-4 items-center">
                        <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Wallet className="w-10 h-10 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground">{t('store.addCreditsDialog.title')}</p>
                          <p className="text-sm text-muted-foreground">
                            {t('store.cart.credit_item_description')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="font-display font-bold text-lg text-primary">
                            {formatPrice(amount)}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeCredit}
                            className="text-destructive hover:text-destructive h-auto p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                );
              }

              return (
                <Caixa id="carrinho.itens.item" repetida key={item.productId}>
                <Card key={item.productId} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.product?.image}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/loja/${item.product?.categorySlug}/produto/${item.product?.slug}`}
                          className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
                        >
                          {item.product?.name}
                        </Link>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {item.product?.shortDescription}
                        </p>
                        <div className="flex gap-2 mt-1">
                          {item.product?.duration && (
                            <Badge variant="outline" className="text-xs">
                              {item.product?.duration}
                            </Badge>
                          )}
                          {item.discountPercent && item.discountPercent > 0 && (
                            <Badge variant="default" className="text-xs bg-success text-success-foreground">
                              {item.discountPercent}% OFF
                            </Badge>
                          )}
                        </div>
                        {problemaDoItem(item.productId) && (
                          <p className="mt-2 text-xs font-medium text-destructive">
                            {problemaDoItem(item.productId)!.message}
                          </p>
                        )}
                      </div>

                      {/* Quantity & Price */}
                      <div className="flex flex-col items-end gap-2">
                        {linhaDaCotacao(item.productId) ? (
                          (() => {
                            // Preço calculado no servidor: é o que vai ser cobrado.
                            const linha = linhaDaCotacao(item.productId)!;
                            const cheio = linha.regularUnitPrice * linha.quantity;
                            return cheio > linha.total ? (
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground line-through">{formatPrice(cheio)}</p>
                                <p className="font-display font-bold text-lg text-success">{formatPrice(linha.total)}</p>
                              </div>
                            ) : (
                              <p className="font-display font-bold text-lg text-primary">{formatPrice(linha.total)}</p>
                            );
                          })()
                        ) : item.discountPercent && item.discountPercent > 0 ? (
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground line-through">
                              {formatPrice(item.product?.price * item.quantity)}
                            </p>
                            <p className="font-display font-bold text-lg text-success">
                              {formatPrice((item.priceWithDiscount ?? item.product?.price ?? 0) * item.quantity)}
                            </p>
                          </div>
                        ) : (
                          <p className="font-display font-bold text-lg text-primary">
                            {formatPrice(item.product?.price * item.quantity)}
                          </p>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(item.productId!, item.quantity - 1)
                            }
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center font-medium">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(item.productId!, item.quantity + 1)
                            }
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.productId!)}
                          className="text-destructive hover:text-destructive h-auto p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </Caixa>
              );
            })}
          </div>
          </Caixa>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Caixa id="carrinho.resumo" miolo>
            <Card className="bg-card border-border sticky top-24">
              <CardHeader>
                <CardTitle className="font-display">Resumo do Pedido</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Coupon */}
                <Caixa id="carrinho.resumo.cupom">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">
                    Cupom de Desconto
                  </Label>
                  {hasCreditItem ? (
                    <p className="text-xs text-muted-foreground italic">
                      {t('store.cart.coupon_disabled_for_credits')}
                    </p>
                  ) : coupon ? (
                    <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/30">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-accent" />
                        <span className="font-medium text-accent">
                          {coupon.code}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeCoupon}
                        className="h-auto p-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite o código"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                      />
                      <Button variant="outline" onClick={handleApplyCoupon}>
                        Aplicar
                      </Button>
                    </div>
                  )}

                </div>
                </Caixa>

                <Separator />

                {/* Payment Method - Only show if finalTotal > 0 */}
                {finalTotal > 0 ? (
                  <Caixa id="carrinho.resumo.pagamento">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-3 block">
                      Forma de Pagamento
                    </Label>
                    
                    {loadingProviders ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : availableProviders.length === 0 ? (
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">
                          Nenhuma forma de pagamento disponível no momento
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {availableProviders.map((provider) =>
                          provider.methods.map((method) => {
                            const isSelected = paymentMethod === method;
                            /*
                             * Rótulos vindos do i18n, não fixos no componente.
                             *
                             * Estavam em português dentro do código, então a
                             * loja em inglês mostrava "Cartão de Crédito" no
                             * meio da página traduzida. O método novo entrou
                             * pelo caminho certo e os antigos vieram junto, já
                             * que é a mesma função.
                             */
                            const getMethodInfo = (m: string) => {
                              const icones: Record<string, string> = {
                                PIX: '💳',
                                CREDIT_CARD: '💳',
                                DEBIT_CARD: '💳',
                                BOLETO: '📄',
                                WALLET: '👛',
                                MERCADO_PAGO: '💰',
                                PAYPAL: '🅿️',
                                PICPAY: '💚',
                                STRIPE: '💳',
                              };
                              const chave = `store.order_detail.cart_${m.toLowerCase()}`;
                              const traduzido = t(chave);

                              const label = traduzido === chave ? m : traduzido;

                              return {
                                icon: icones[m] ?? '💳',
                                /*
                                 * Quando o meio tem o mesmo nome do gateway, o
                                 * botão mostraria "PicPay" em cima de "PicPay".
                                 * Acontece desde que o PicPay passou a oferecer
                                 * três meios: PIX e cartão ficam claros, mas a
                                 * carteira repetiria o nome.
                                 */
                                label:
                                  label.toLowerCase() === provider.displayName.toLowerCase()
                                    ? t('store.order_detail.cart_wallet_generic')
                                    : label,
                              };
                            };
                            const methodInfo = getMethodInfo(method);
                            
                            return (
                              <button
                                key={`${provider.provider}-${method}`}
                                type="button"
                                onClick={() => { setPaymentMethod(method); setPaymentProvider(provider.provider); }}
                                className={`
                                  relative flex items-center gap-4 p-4 rounded-lg border-2 transition-all
                                  ${isSelected 
                                    ? 'border-accent bg-accent/10 shadow-md' 
                                    : 'border-border bg-card hover:border-accent/50 hover:bg-accent/5'}
                                `}
                              >
                                {/* Provider Logo/Icon */}
                                <div className={`
                                  flex items-center justify-center w-12 h-12 rounded-lg
                                  ${isSelected ? 'bg-accent/20' : 'bg-muted'}
                                `}>
                                  {provider.provider === 'mercadopago' ? (
                                    <img 
                                      src="/payments/mercadopago.png" 
                                      alt="Mercado Pago" 
                                      className="w-24 h-24 object-contain"
                                    />
                                  ) : (
                                    <span className="text-2xl">{methodInfo.icon}</span>
                                  )}
                                </div>
                                
                                {/* Method Details */}
                                <div className="flex-1 text-left">
                                  <div className="font-semibold text-foreground">
                                    {provider.displayName}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {methodInfo.label}
                                  </div>
                                </div>
                                
                                {/* Check indicator */}
                                {isSelected && (
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-accent">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                  </div>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/*
                      Campo de CPF, só quando o método escolhido exige.
                      Mostrar sempre pediria um dado pessoal a quem paga por um
                      meio que não precisa dele.
                    */}
                    {exigeDocumento && (
                      <div className="mt-4 space-y-2">
                        <label
                          htmlFor="buyerDocument"
                          className="text-sm font-medium text-foreground"
                        >
                          {t('store.cart.document_label')}
                        </label>
                        <input
                          id="buyerDocument"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={18}
                          value={buyerDocument}
                          onChange={(e) => setBuyerDocument(e.target.value)}
                          placeholder={t('store.cart.document_placeholder')}
                          className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-accent"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('store.cart.document_help')}
                        </p>
                      </div>
                    )}

                    {/*
                      O formulário de cartão vem depois do documento: o CPF ali
                      em cima é o do portador, e o SDK precisa dele para
                      tokenizar.
                    */}
                    {exigeCartao && (
                      <CardForm
                        valor={dadosCartao}
                        onChange={setDadosCartao}
                        parcelas={parcelas}
                        onParcelasChange={setParcelas}
                        maxParcelas={12}
                        desabilitado={loading}
                      />
                    )}
                  </div>
                  </Caixa>
                ) : (
                  <Caixa id="carrinho.resumo.gratis">
                  <div className="p-4 bg-accent/10 rounded-lg border border-accent/30">
                    <p className="text-sm text-accent font-medium text-center">
                      🎉 Pedido 100% gratuito! Nenhum pagamento necessário.
                    </p>
                  </div>
                  </Caixa>
                )}

                <Separator />

                {/* Credits Section */}
                {isAuthenticated && userCredits > 0 && !hasCreditItem && (
                  <Caixa id="carrinho.resumo.creditos">
                  <div className="space-y-3">
                    <Label className="text-sm text-muted-foreground block">
                      Usar Créditos ({formatCredits(userCredits)} disponíveis)
                    </Label>
                    
                    {!useCredits ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={handleUseAllCredits}
                            disabled={maxCreditsToUse === 0}
                          >
                            Usar Tudo ({formatCredits(maxCreditsToUse)})
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="0,00"
                              value={customCreditsInput}
                              onChange={handleCreditsInputChange}
                              className="pl-9"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleUseCustomCredits}
                            disabled={!customCreditsInput}
                          >
                            Aplicar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/30">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-accent" />
                          <span className="text-sm font-medium">
                            Usando {formatCredits(creditsAmount)} em créditos
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveCredits}
                          className="text-destructive hover:text-destructive"
                        >
                          Remover
                        </Button>
                      </div>
                    )}
                  </div>
                  </Caixa>
                )}

                <Separator />

                {/* Price Summary */}
                <Caixa id="carrinho.resumo.totais">
                <div className="space-y-2">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-accent">
                      <span>Desconto (Cupom)</span>
                      <span>-{formatPrice(discount)}</span>
                    </div>
                  )}
                  {creditsAmount > 0 && (
                    <div className="flex justify-between text-accent">
                      <span>Créditos Utilizados</span>
                      <span>-{formatCredits(creditsAmount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-display font-bold text-lg">
                    <span>Total</span>
                    <span className="text-primary">{formatPrice(finalTotal)}</span>
                  </div>
                </div>
                </Caixa>

                <Separator />

                {/* Terms */}
                <Caixa id="carrinho.resumo.termos">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                  />
                  <Label
                    htmlFor="terms"
                    className="text-sm text-muted-foreground leading-tight cursor-pointer"
                  >
                    Li e aceito os{' '}
                    <Link to="/termos" className="text-primary hover:underline">
                      Termos de Serviço
                    </Link>{' '}
                    e a{' '}
                    <Link to="/privacidade" className="text-primary hover:underline">
                      Política de Privacidade
                    </Link>
                  </Label>
                </div>
                </Caixa>
              </CardContent>

              {problemas.length > 0 && (
                <div className="mx-6 mb-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  <p className="font-medium">{t('store.cart.fix_problems')}</p>
                  <ul className="mt-1 list-disc pl-4 space-y-0.5">
                    {problemas.map((p) => (
                      <li key={`${p.code}-${p.productId}`}>{p.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Caixa id="carrinho.finalizar">
              <CardFooter>
                <Button
                  className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={!acceptTerms || (finalTotal > 0 && !paymentMethod) || loading || quoting || !quote || problemas.length > 0 || (finalTotal > 0 && loadingProviders)}
                >
                  {loading || quoting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : finalTotal === 0 ? (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Confirmar Pedido Gratuito
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Finalizar Compra
                    </>
                  )}
                </Button>
              </CardFooter>
              </Caixa>
            </Card>
            </Caixa>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        checkoutData={checkoutData}
        method={paymentMethod as PaymentMethod}
        orderId={currentOrderId}
        primaryDomain={currentPrimaryDomain}
        onSuccess={handleCheckoutSuccess}
      />

      {/*
        Fechar o Lightbox não cancela o pagamento: quem confirma é o webhook.
        Por isso o jogador volta para o carrinho com o pedido já criado, e não
        para uma tela de "cancelado" que seria mentira.
      */}
      {lightboxUrl && (
        <PicPayLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}
    </div>
  );
}
